import {
	ActionButton,
	Button,
	StatusLight,
	TextArea,
} from '@react-spectrum/s2';
import { useCallback, useEffect, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { useBridgeApi } from '@/api/BridgeApi';
import { useGoToNextStep } from '@/components/artifact/useGoToNextStep.ts';
import { useStepSubmitted } from '@/components/artifact/useStepSubmitted';
import { ChatPanelLayout } from '@/spaces/chat/panels/ChatPanelLayout.tsx';
import {
	OUTCOME_LABEL,
	OUTCOME_VARIANT,
} from '@/spaces/chat/panels/ownerActionOutcome.ts';
import { cn } from '@/utils/cn.ts';

import type {
	Action as ProcessAction,
	OwnerActionOutcome,
	RequestOwnerActionConfig,
} from '../../../../../bridge/types/action.ts';
import { latestOwnerActionResponse } from '../../../../../bridge/utils/processClassification.ts';

import { ownerAskContextLine } from './ownerActionFromPanel.ts';

interface OwnerActionPanelProps {
	action: ProcessAction;
	onBack?: () => void;
	onClose: () => void;
	style?: React.CSSProperties;
}

const Eyebrow = ({ children }: { children: React.ReactNode }) => (
	<p
		className="text-xs font-semibold tracking-wide text-(--text-muted)
			uppercase"
	>
		{children}
	</p>
);

// These asks arrive as authored markdown — numbered checklists, bullets, blank
// lines between paragraphs — so a plain <p> collapses the whole brief into one
// run-on wall of text. `pre-line` is scoped to p/li rather than the container
// because react-markdown emits newline text nodes BETWEEN blocks, which a
// container-level pre-line would render as stray blank lines; scoping it also
// recovers the lines of bullets written with a literal "•", which markdown
// treats as one paragraph.
const PROSE_CLASS = cn(
	'text-sm leading-relaxed break-words',
	'[&_p]:my-2 [&_p]:whitespace-pre-line [&>:first-child]:mt-0',
	'[&>:last-child]:mb-0',
	'[&_strong]:font-semibold [&_em]:italic',
	'[&_a]:text-(--color-beacon-accent) [&_a]:underline',
	'[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5',
	'[&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5',
	'[&_li]:my-1 [&_li]:leading-relaxed [&_li]:whitespace-pre-line',
);

// The prompts now forbid emoji here, but asks authored before that rule are
// already stored, so strip the decorative ones at render. These sit where a
// bullet or a "note:" would — starting a line, or opening a sentence mid-
// paragraph — and the prose reads correctly without them. Arrows are left alone
// on purpose: "questions → result → CTA" is meaning, not decoration.
const PICTOGRAPH = '\\p{Extended_Pictographic}\\uFE0F?';
const DECORATIVE_MARKER = new RegExp(
	// line start, keeping any real list marker; or just after sentence-ending
	// punctuation, keeping the punctuation and its space
	`(^\\s*(?:[-*•]|\\d+[.)])?\\s*|(?<=[.!?]\\s))(?:${PICTOGRAPH}\\s*)+`,
	'gmu',
);

const stripMarkerEmoji = (text: string) =>
	text.replace(DECORATIVE_MARKER, '$1');

// The date is a footnote to the answer, so an unparseable one is dropped rather
// than shown: "Reported Invalid Date" makes the owner doubt whether we recorded
// what they told us, when only the timestamp is at fault.
const formatReportedOn = (iso: string | undefined): string | null => {
	if (!iso) return null;
	const at = new Date(iso);
	return Number.isNaN(at.getTime()) ? null : at.toLocaleDateString();
};

// An owner can answer without typing: a yes/no ask records `confirmed` only, and
// a decline records neither `confirmed` nor `text`. Without these sentences the
// answer block renders a question and a date around nothing, which reads as a
// lost answer rather than a recorded one.
const NO_TEXT_ANSWER: Record<OwnerActionOutcome, string> = {
	done: 'You told us you did this.',
	not_done: 'You told us this has not happened yet.',
	declined: 'You told us you are not doing this.',
};

const Prose = ({ body, muted }: { body: string; muted?: boolean }) => (
	<div
		className={cn(
			PROSE_CLASS,
			muted ? 'text-(--text-muted)' : 'text-(--text-primary)',
		)}
	>
		<Markdown remarkPlugins={[remarkGfm]}>{stripMarkerEmoji(body)}</Markdown>
	</div>
);

// The footer splits by KIND, not by emphasis alone. The left slot holds the one
// control that is not an answer to the question, so distance says it belongs to a
// different kind. `ChatPanelLayout` right-aligns its footer children, and every
// other panel shares that layout, so the split lives here as a full-width child
// rather than as an edit to the shared shell.
const FooterRow = ({
	quiet,
	answers,
}: {
	quiet?: React.ReactNode;
	answers: React.ReactNode;
}) => (
	<div className="flex w-full items-center justify-between gap-2">
		<div className="flex items-center">{quiet}</div>
		<div className="flex items-center gap-2">{answers}</div>
	</div>
);

export const OwnerActionPanel = ({
	action,
	onBack,
	onClose,
	style,
}: OwnerActionPanelProps) => {
	const bridgeApi = useBridgeApi();
	const goToNextItem = useGoToNextStep();
	const content = action.config as RequestOwnerActionConfig;
	const response = latestOwnerActionResponse(action);

	// Accepting an ask is a finished setup step, exactly like approving a post.
	// Every other step panel confirms the choice and offers the next step, so
	// this one does too.
	const { submittedView, markSubmitted } = useStepSubmitted({
		title: 'Your part confirmed',
		summary: 'Ready for the next step in your campaign setup.',
		onClose,
		onGoToNextStep: () => {
			if (action.process_id) goToNextItem(action.process_id, action.id);
		},
	});

	const [draft, setDraft] = useState('');
	// Reopens the reply form over a recorded answer, so a wrong answer is
	// correctable without a fourth control in the footer row.
	const [editing, setEditing] = useState(false);
	// Which control the owner pressed, or null. The ref is the real guard: a
	// disabled attribute only takes effect on the next render, so two presses in
	// the same tick both reach the reducer, and the second one overwrites the
	// first answer with a different outcome.
	const [pending, setPending] = useState<string | null>(null);
	const pendingRef = useRef(false);
	const reportedAtAtPress = useRef<string | undefined>(undefined);

	const answered = response !== null;
	const outcome = response?.outcome ?? 'done';
	const reportedOn = formatReportedOn(response?.reported_at);
	const isConfirm = content.response_shape === 'confirm';

	const report = useCallback(
		(
			key: string,
			payload: {
				outcome: OwnerActionOutcome;
				confirmed?: boolean;
				text?: string;
			},
		) => {
			if (pendingRef.current) return;
			pendingRef.current = true;
			reportedAtAtPress.current = response?.reported_at;
			setPending(key);
			bridgeApi.sendAction({
				type: 'do_report_owner_action',
				action_id: action.id,
				source: 'ui',
				...payload,
			});
			// Every panel in the setup flow marks its own card row complete once
			// the owner has dealt with it; without this the campaign can never
			// become ready to go live (isProcessReadyToActivate requires every
			// row in completed_item_ids), so an all-asks campaign would stall at
			// "proposed" forever.
			if (action.process_id) {
				bridgeApi.sendAction({
					type: 'do_mark_setup_item_completed',
					process_id: action.process_id,
					item_id: action.id,
				});
			}
		},
		[action.id, action.process_id, bridgeApi, response?.reported_at],
	);

	// Release the guard when the write lands, and only then close the form. The
	// pressed control has to keep its own in-flight state until the answer is
	// really recorded, or the owner sees the panel settle before anything is
	// stored. `answered` cannot serve as the signal: every corrective press starts
	// and ends on an answered ask, so it never flips, and `ChatPanel` keys this
	// panel by ask id, so a delta does not remount it. The reducer stamps a fresh
	// `reported_at` on every write, so that value always moves.
	useEffect(() => {
		if (pending === null) return;
		if (response?.reported_at !== reportedAtAtPress.current) {
			pendingRef.current = false;
			setPending(null);
			setEditing(false);
			setDraft('');
		}
	}, [pending, response?.reported_at]);

	// SETUP vs LIVE are two different asks of the owner, and conflating them was
	// wrong: during setup the owner is only agreeing to TAKE ON the step, so
	// asking "did you do it?" before the campaign is even active makes no sense.
	// The report-back belongs after activation. Read the phase from the parent
	// process's own status rather than inventing a flag.
	const snapshot = action.process_id
		? bridgeApi.user_state?.process_setup_snapshots?.[action.process_id]
		: null;
	const contextLine = ownerAskContextLine(bridgeApi.user_state, action);
	const inSetup =
		snapshot != null &&
		(snapshot.status === 'proposed' || snapshot.status === 'draft');
	const acceptedInSetup = Boolean(
		action.process_id && snapshot?.completed_item_ids?.includes(action.id),
	);
	const skipped = Boolean(
		action.process_id && snapshot?.skipped_action_ids?.includes(action.id),
	);

	// The reply form is open while the ask is unanswered, and again while the
	// owner edits a recorded answer. It is the ONLY place the note box exists, so
	// it is also what decides whether a payload may carry `text` — a hidden box
	// must never attach a stale draft to an answer.
	const formOpen = !inSetup && !skipped && (!answered || editing);

	// No badge during setup at all. The footer states the choice in buttons that
	// name it, and the setup card row already carries the row's own status. No
	// badge while a live ask is still open either: the owner's own words for that
	// were "we dont need the waiting on you status". The filled control now
	// carries that signal.
	const statusLight = answered ? (
		<StatusLight variant={OUTCOME_VARIANT[outcome]} size="S">
			{OUTCOME_LABEL[outcome]}
		</StatusLight>
	) : skipped && !inSetup ? (
		<StatusLight variant="neutral" size="S">
			Skipped
		</StatusLight>
	) : null;

	// During setup the only question is "are you taking this on?" — accepting
	// completes the row exactly like approving a post does, and declining skips
	// it exactly like every other row's Skip. No outcome is recorded here,
	// because nothing has happened yet.
	const acceptStep = useCallback(() => {
		if (!action.process_id) return;
		bridgeApi.sendAction({
			type: 'do_mark_setup_item_completed',
			process_id: action.process_id,
			item_id: action.id,
		});
		markSubmitted();
	}, [action.id, action.process_id, bridgeApi, markSubmitted]);

	// A skip advances without a success screen. That is what the artifact rows
	// already do, and a skip is not an accomplishment to confirm.
	const skipStep = useCallback(() => {
		if (!action.process_id) return;
		bridgeApi.sendAction({
			type: 'do_skip_action',
			process_id: action.process_id,
			action_id: action.id,
		});
		goToNextItem(action.process_id, action.id);
	}, [action.id, action.process_id, bridgeApi, goToNextItem]);

	const unskipStep = useCallback(() => {
		if (!action.process_id) return;
		bridgeApi.sendAction({
			type: 'do_unskip_action',
			process_id: action.process_id,
			action_id: action.id,
		});
	}, [action.id, action.process_id, bridgeApi]);

	const typed = draft.trim();
	// A text ask exists to collect the numbers, so its commit control waits for
	// them. This applies only while the box is on screen: no answered row disables
	// a control, or the one row that must offer success would offer only a refusal.
	const commitDisabled = formOpen && !isConfirm && typed.length === 0;
	// Editing is stricter, and for both shapes. Saving an empty box would replace
	// the words the owner already gave with nothing, and the only copy of those
	// words is the one this form is about to overwrite.
	const saveDisabled = typed.length === 0;
	const busy = pending !== null;

	const answerPayload = useCallback(
		(base: { outcome: OwnerActionOutcome; confirmed?: boolean }) =>
			formOpen && typed ? { ...base, text: typed } : base,
		[formOpen, typed],
	);

	const didThis = () =>
		report('done', answerPayload({ outcome: 'done', confirmed: true }));
	const notYet = () =>
		report(
			'not_done',
			answerPayload({ outcome: 'not_done', confirmed: false }),
		);
	const notDoingThis = () =>
		report('declined', answerPayload({ outcome: 'declined' }));
	// Editing changes the words, never the outcome. Changing the outcome is what
	// the answered rows' own controls are for.
	const saveNote = () =>
		report(
			'save',
			answerPayload({
				outcome,
				...(response?.confirmed != null
					? { confirmed: response.confirmed }
					: {}),
			}),
		);

	const startEditing = () => {
		setDraft(response?.text ?? '');
		setEditing(true);
	};
	const cancelEditing = () => {
		setEditing(false);
		setDraft('');
	};

	const DidThisButton = (
		<Button
			variant="accent"
			isPending={pending === 'done'}
			isDisabled={commitDisabled || (busy && pending !== 'done')}
			onPress={didThis}
		>
			I did this
		</Button>
	);
	const NotYetButton = (
		<Button
			variant="secondary"
			isPending={pending === 'not_done'}
			isDisabled={busy && pending !== 'not_done'}
			onPress={notYet}
		>
			Not yet
		</Button>
	);
	const NotDoingThisButton = (
		<ActionButton isQuiet isDisabled={busy} onPress={notDoingThis}>
			Not doing this
		</ActionButton>
	);
	const EditAnswerButton = (
		<Button variant="secondary" isDisabled={busy} onPress={startEditing}>
			Edit answer
		</Button>
	);

	const setupRenderer = skipped
		? () => (
				<Button variant="accent" onPress={unskipStep}>
					Actually, I&apos;ll do this
				</Button>
			)
		: acceptedInSetup
			? () => (
					<Button variant="secondary" onPress={skipStep}>
						Skip this step
					</Button>
				)
			: () => (
					<>
						<Button variant="secondary" onPress={skipStep}>
							Skip this step
						</Button>
						<Button variant="accent" onPress={acceptStep}>
							I&apos;ll do this
						</Button>
					</>
				);

	// One label per outcome, in every row that can record it. Two labels for one
	// outcome is what made the old footer unreadable: "It didn't happen" and
	// "Skip this one" recorded different things and read as the same choice.
	const liveRenderer = () => {
		if (editing) {
			return (
				<FooterRow
					quiet={
						<ActionButton isQuiet isDisabled={busy} onPress={cancelEditing}>
							Cancel
						</ActionButton>
					}
					answers={
						<Button
							variant="accent"
							isPending={pending === 'save'}
							isDisabled={saveDisabled || (busy && pending !== 'save')}
							onPress={saveNote}
						>
							Save
						</Button>
					}
				/>
			);
		}
		if (!answered) {
			if (skipped) {
				return (
					<FooterRow
						answers={
							<>
								<Button
									variant="secondary"
									isDisabled={busy}
									onPress={unskipStep}
								>
									I&apos;ll do this
								</Button>
								<Button variant="accent" isDisabled>
									I did this
								</Button>
							</>
						}
					/>
				);
			}
			return (
				<FooterRow
					quiet={NotDoingThisButton}
					answers={
						<>
							{NotYetButton}
							{DidThisButton}
						</>
					}
				/>
			);
		}
		// A finished record carries no filled control. The header already closes
		// the panel, and a "next step" control here only closed it under another
		// name (useGoToNextStep sends do_close_chat_panel once no step is left).
		if (outcome === 'done') {
			return (
				<FooterRow quiet={NotDoingThisButton} answers={EditAnswerButton} />
			);
		}
		// `not_done` is the one outcome that keeps the ask open, so this row MUST
		// offer success. It could not before: the single control moved the owner
		// to `declined`.
		if (outcome === 'not_done') {
			return (
				<FooterRow
					quiet={NotDoingThisButton}
					answers={
						<>
							{EditAnswerButton}
							{DidThisButton}
						</>
					}
				/>
			);
		}
		return (
			<FooterRow
				answers={
					<>
						{EditAnswerButton}
						{DidThisButton}
					</>
				}
			/>
		);
	};

	const briefCard = (
		<section className="glass-card-sm flex min-w-0 flex-col gap-3 p-4">
			<div className="flex min-w-0 items-baseline justify-between gap-2">
				<Eyebrow>What to do</Eyebrow>
				{/* The deadline limits the instruction, so it belongs beside the
				    instruction's own label. As a trailing line it read as an orphan. */}
				{content.due_window ? (
					<p className="shrink-0 text-xs text-(--text-muted)">
						{content.due_window}
					</p>
				) : null}
			</div>
			{/* Full text, never clamped: this is the whole point of the panel. */}
			<Prose body={content.instruction} />
			{/* The moves the plan already wrote out. Detail inside this one ask — never
			    separate things to answer, so nothing here is a control. */}
			{content.checklist && content.checklist.length > 0 ? (
				<ol
					data-testid="owner-action-checklist"
					className="flex list-decimal flex-col gap-2 border-t
						border-(--glass-border) pt-3 pl-5 text-sm text-(--text-primary)"
				>
					{content.checklist.map((item, i) => (
						<li key={`${item.action}-${i}`} className="pl-1">
							<span>{item.action}</span>
							{item.needs ? (
								<span className="mt-0.5 block text-xs text-(--text-muted)">
									Needs {item.needs}
								</span>
							) : null}
						</li>
					))}
				</ol>
			) : null}
		</section>
	);

	// The owner's own words, on the surface this product already uses for the
	// owner's own words (the chat bubble at UserMessage.tsx). As bare body copy it
	// read as smaller than the question asking for it.
	const answerBlock = (
		<div
			data-testid="owner-action-answer"
			className="flex min-w-0 flex-col gap-2 rounded-3xl bg-gray-100 p-4"
		>
			<p className="text-xs leading-relaxed text-(--text-muted)">
				We asked: {content.report_prompt}
			</p>
			<p
				className="text-base leading-relaxed font-medium break-words
					whitespace-pre-wrap text-(--text-primary)"
			>
				{response?.text?.trim() ? response.text : NO_TEXT_ANSWER[outcome]}
			</p>
			{reportedOn ? (
				<p className="text-xs text-(--text-muted)">Reported {reportedOn}</p>
			) : null}
		</div>
	);

	const reportForm = (
		<div className="flex min-w-0 flex-col gap-2">
			<Eyebrow>Tell us how it went</Eyebrow>
			<Prose body={content.report_prompt} />
			<TextArea
				aria-label={isConfirm ? 'Add a note (optional)' : 'Your update'}
				label={isConfirm ? 'Add a note (optional)' : undefined}
				placeholder="A sentence or two is plenty"
				value={draft}
				onChange={setDraft}
			/>
		</div>
	);

	return (
		<ChatPanelLayout
			title="Your part"
			// Where the owner is, in the layout's own pinned slot. The body scrolls and the
			// report-back question sits at its bottom, so a line inside the body would leave the
			// screen exactly when they answer. Suppressed on the confirmation screen, which
			// states one outcome and needs no breadcrumb above it.
			description={submittedView ? undefined : contextLine}
			headerTagRender={() => (submittedView ? null : statusLight)}
			actionRenderer={
				submittedView ? undefined : inSetup ? setupRenderer : liveRenderer
			}
			onBack={onBack}
			onClose={onClose}
			style={style}
		>
			{submittedView ?? (
				<div className="flex min-w-0 flex-col gap-5 p-4">
					{/* Once an answer exists it is the record and the brief is history, so
					    the answer leads. While the form is open the brief is still
					    instructions, so it leads instead. */}
					{answered && !editing ? (
						<>
							{answerBlock}
							{briefCard}
						</>
					) : (
						<>
							{briefCard}
							{inSetup ? (
								<div className="flex min-w-0 flex-col gap-2">
									<Eyebrow>What we&apos;ll ask you later</Eyebrow>
									<Prose body={content.report_prompt} muted />
									<p className="text-xs text-(--text-muted)">
										Once the campaign is live, come back and let us know how it
										went.
									</p>
								</div>
							) : skipped ? null : (
								reportForm
							)}
						</>
					)}
				</div>
			)}
		</ChatPanelLayout>
	);
};
